import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "../../../../../../app/helpers";
import { RootState } from "../../../../../../app/rootReducer";
import { SelectionPrompt2 as SelectionPrompt } from "./SelectionPrompt(2)";
import {
  handleSelection,
  selectableByVariant,
  selectedEntry
} from "../Functions/SelectFloorPlanView.functions";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { ISeatStatus } from "../../../../Domain/Types/FloorPlan/SeatStatus.type";
import { hasServicePlanMandatory } from "../BookingServicePrompt/functions/hasMandatory.function";
import type { FilterCompatibility } from "../../../../../../features/Booking-Form/functions/booking-filter-compatibility.functions";

interface Props {
  floorPlan: IFloorPayload;
  floorPlanForService: IFloorPayload | undefined;
  seatStatus: ISeatStatus;
  selectedPlace: number | undefined;
  selectedZone: number | undefined;
  bookingInputs: BookingInputs;
  userIndex: number;
  isOccupiedDataLoading: boolean;
  setSelectedPlace: (place: number | undefined) => void;
  setSelectedZone: (zone: number | undefined) => void;
  setUserIndex: (i: number) => void;
  onSingleSelect?: (placeInventoryId: number) => void;
  onTeamSelect?: (index: number, placeInventoryId: number) => void;
  onNextStep: () => void;
  openTimeDialog: (selectedEntryId: number) => void;
  allowInactiveResourceBooking?: boolean;
  disabledInventoryIdsForAdminSelection?: number[];
  filterCompatibility?: FilterCompatibility;
}

export function ResponsiveSelectionPrompt({
  floorPlan,
  floorPlanForService,
  seatStatus,
  selectedPlace,
  selectedZone,
  bookingInputs,
  userIndex,
  isOccupiedDataLoading,
  setSelectedPlace,
  setSelectedZone,
  setUserIndex,
  onSingleSelect,
  onTeamSelect,
  onNextStep,
  openTimeDialog,
  allowInactiveResourceBooking = false,
  disabledInventoryIdsForAdminSelection,
  filterCompatibility
}: Readonly<Props>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  // when is mobile or tablet, display selection prompt as dialog in the center
  const isBrowser = useMediaQuery(theme.breakpoints.up(1200));

  const { bookingType, conferenceService } = useSelector(
    (state: RootState) => state.booking.inputs
  );
  const { userInformation } = useSelector((state: RootState) => state.login);

  const isSelectedEntry = (() => selectedEntry(floorPlan, selectedPlace, selectedZone))();
  const scheduleEntry = isSelectedEntry.place || isSelectedEntry.zone;

  const isSelectableByVariant = (() =>
    selectableByVariant(
      selectedEntry(floorPlan, selectedPlace, selectedZone),
      seatStatus,
      bookingType,
      {
        allowInactiveResourceBooking,
        disabledInventoryIds: disabledInventoryIdsForAdminSelection
      }
    ))();
  const conferenceServiceInventoryKey = scheduleEntry?.inventory?.id || scheduleEntry?.inventoryId;
  const hasConferenceService =
    conferenceServiceInventoryKey != null && !!conferenceService?.[conferenceServiceInventoryKey];

  const mandatory = hasServicePlanMandatory({ floorPlanForService, selectedZone });
  const isShowingServiceModalFirst =
    floorPlanForService && mandatory && !hasConferenceService ? 800 : undefined;

  const handleConfirmSelection = () => {
    handleSelection(
      bookingInputs,
      userIndex,
      floorPlan,
      { selectedPlace, selectedZone },
      { setSelectedPlace, setUserIndex },
      { onSingleSelect, onTeamSelect, onNextStep },
      { enqueueSnackbar, t }
    );
  };

  const closeSelectionPrompt = (e: any) => {
    e.stopPropagation();
    setSelectedPlace(undefined);
    setSelectedZone(undefined);
  };

  return (
    <Dialog
      open
      maxWidth={isBrowser ? false : "md"}
      fullWidth={!isBrowser}
      onClose={closeSelectionPrompt}
      transitionDuration={isShowingServiceModalFirst}
    >
      <SelectionPrompt
        selected={isSelectedEntry}
        userIndex={userIndex}
        userId={userInformation.sub}
        openTimeDialog={openTimeDialog}
        selectableByVariant={isSelectableByVariant}
        onConfirm={handleConfirmSelection}
        onClose={closeSelectionPrompt}
        bookingInputs={bookingInputs}
        selectedZone={selectedZone}
        isOccupiedDataLoading={isOccupiedDataLoading}
        floorPlanForService={floorPlanForService}
        floorInventoryId={floorPlan.floorInventoryId}
        filterCompatibility={filterCompatibility}
      />
    </Dialog>
  );
}
